import { useApolloClient } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { EditedHouseholdModelPortfolio } from './householdAllocations';
import {
  loadHouseholdAttachedModel,
  stagedHouseholdBaseline,
  stagedHouseholdDraft,
} from './householdDraftStaging';

export type HouseholdModelPortfolioDraftError =
  | 'MISSING_HOUSEHOLD'
  | 'MISSING_MODEL'
  | 'LOAD_FAILED';

export type HouseholdModelPortfolioDraftState = {
  /** Carries the securities step's rows once that step has been through. */
  draft?: EditedHouseholdModelPortfolio;
  /**
   * What the household had before this workflow, for steps that show the change
   * rather than just the result. Absent on a branch that seeded the draft
   * elsewhere, which is why callers fall back to showing the draft alone.
   */
  baseline?: HouseholdModelPortfolioModel;
  loading: boolean;
  error?: HouseholdModelPortfolioDraftError;
  retry: () => void;
};

/**
 * Guarantees the workflow context holds a draft of the household's model
 * portfolio.
 *
 * The Change branch already seeds one when the advisor picks a new parent
 * model, but the Edit and Change IPS branches jump straight here, so the
 * household's currently attached model has to be loaded and staged. Later steps
 * read the same draft, which is why it is written to context rather than kept
 * locally.
 *
 * The draft's `id` is not a stable identity: on the Change branch it is the
 * base model the advisor picked, and here it is the clone already attached to
 * the household. Anything that has to name the model being changed wants
 * `entityBoundModelPortfolioId`, not `draft.id`.
 */
export const useHouseholdModelPortfolioDraft = (
  clientGroupId?: string,
): HouseholdModelPortfolioDraftState => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const graphqlClient = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HouseholdModelPortfolioDraftError>();
  // A load is attempted once per mount unless the caller retries: a household
  // with no attached model would otherwise re-request on every render, since
  // nothing lands in context.
  const attempted = useRef(false);

  // A draft is a copy of a model, and says which one. The securities step
  // shares this context key with the shelf flow and reports its rows there on
  // arriving, so a step ordered ahead of the one that stages the household's
  // model can leave a shape like `{ securities: [] }` behind. Read as a draft
  // that would stop the household's own model ever being fetched, and leave
  // the steps after it working from nothing.
  const staged = workflowData.modelPortfolioDetails;
  const draft = staged?.id ? staged : undefined;
  const householdId =
    clientGroupId ?? workflowData.currentHouseholdClientGroupId;

  const loadDraft = useCallback(async () => {
    // Claiming the attempt only once there is a household to load for, so an
    // id that arrives a render late is still picked up.
    if (!householdId) {
      setError('MISSING_HOUSEHOLD');
      return;
    }

    attempted.current = true;
    setError(undefined);
    setLoading(true);

    try {
      const { model, householdOrganizationId } =
        await loadHouseholdAttachedModel(graphqlClient, householdId);

      if (!model) {
        setError('MISSING_MODEL');
        return;
      }

      // Two clones, not one shared reference: the draft is edited from here on
      // and the baseline has to keep saying what the household had.
      await setWorkflowData((previousData) => ({
        ...previousData,
        modelPortfolioDetails: stagedHouseholdDraft(model),
        householdModelPortfolioBaseline: stagedHouseholdBaseline(model),
        currentHouseholdClientGroupId: householdId,
        // The commit sites scope their mutations to the household's own
        // organization, never the advisor's active one.
        entityOrganizationId:
          previousData.entityOrganizationId ??
          householdOrganizationId ??
          undefined,
      }));
    } catch (caught) {
      datadogLogs.logger.error(
        'Household model portfolio draft load failed',
        { clientGroupId: householdId },
        caught instanceof Error ? caught : undefined,
      );
      setError('LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, [householdId, graphqlClient, setWorkflowData]);

  useEffect(() => {
    if (draft || attempted.current) return;
    void loadDraft();
  }, [draft, loadDraft]);

  return {
    draft,
    baseline: workflowData.householdModelPortfolioBaseline,
    loading,
    error,
    retry: () => {
      attempted.current = false;
      void loadDraft();
    },
  };
};
